import { gsap } from "gsap";
import SplitType from 'split-type';
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { cloneElement, useLayoutEffect, useEffect, useRef, useState, useCallback, Fragment, lazy, Suspense } from 'react';
import logo from './logo.svg';
import dudu_photo from './midia/profile-cutout-mask.png';
import dudu_original from './midia/profile-original.png';
import barbershop_thumb from './midia/barbershop_thumb.png';
import { login, bookings, gallery, homeServices, professionals, schedule, confirmationModal, scheduleSettings, scheduleSettingsModal } from './midia/app_barbershop';
import { login_hub4you, applyModal, brandManual, company, emptyNotifications, home, newPostModal, notifications, profile, fullProfile } from './midia/app_hub4you';
import hub4you_thumb from './midia/hub4you_thumb.png';
import BgDottedCircle from './dotted-circle.jsx';
import './i18n/index.js';
import Modal from './components/Modal.tsx';
import { useTranslation } from 'react-i18next';
import './App.css';
import TechIcon from './components/TechIcon';
import { stackIcons } from './data/svgIcons.jsx';
import { ScrollTrigger, SplitText } from './lib/gsap';
import { LINKS } from './data/content';
import LiquidGlass from './components/LiquidGlass';
import BrandButton, { ArrowIcon } from './components/BrandButton';
import ClickSpark from './components/ClickSpark';
import LangSwitch from './components/LangSwitch';
import LangTransition from './components/LangTransition';
import { GithubIcon, LinkedinIcon, DownloadIcon } from './components/SocialIcons';
import Manifesto from './sections/Manifesto';
import Stack from './sections/Stack';
import Workflow from './sections/Workflow';
import About from './sections/About';
import Contact from './sections/Contact';
import { coinsState } from './lib/heroState';
import { hasWebGL } from './lib/webgl';

// three.js only loads for the hero coins and sparkles, after first paint
const HeroCoins = lazy(() => import('./components/HeroCoins'));
const Sparkles = lazy(() => import('./components/Sparkles'));

const RING = 2 * Math.PI * 19; // circumference of the header logo's scroll-progress ring

function App() {

  gsap.registerPlugin(ScrollToPlugin);

  const BackgroundCircles = () => {
    return (
      <>
        <div
          className="bg-blurred-circle absolute left-0 top-0 w-[875px] h-[1400px] rounded-full -translate-x-1/2 -translate-y-1/2 pointer-events-none"
        />
        <div
          className="bg-blurred-circle absolute right-0 top-[500px] w-[875px] h-[875px] rounded-full translate-x-1/2 pointer-events-none"
        />
      </>
    );
  };

  function showSidebar() {
    const sidebar = document.querySelector('.sidebar')
    sidebar.style.display = 'flex'
  }

  function hideSidebar() {
    const sidebar = document.querySelector('.sidebar')
    sidebar.style.display = 'none'
  }

  const { i18n, t } = useTranslation();
  const [languageSelected, setLanguageSelected] = useState(i18n.language);
  const langCover = useRef(null);
  const langBusy = useRef(false);
  const handleChangeLanguage = useCallback(async () => {
    if (langBusy.current) return;
    langBusy.current = true;
    const newLanguage = languageSelected === "en-US" ? "pt-BR" : "en-US";
    const swap = () => {
      // When changing language, close the modal if it's open
      closeModal();
      // Back to the top (behind the cover) so the replayed intro animation is visible
      window.scrollTo(0, 0);
      i18n.changeLanguage(newLanguage);
      setLanguageSelected(newLanguage);
    };
    if (langCover.current) await langCover.current(swap, newLanguage);
    else swap();
    langBusy.current = false;
  }, [languageSelected, i18n]);

  const handleLinkClick = (e) => {
    e.preventDefault();
    const target = e.currentTarget.getAttribute('href');
    gsap.to(window, { duration: 1, scrollTo: target, ease: 'power2.inOut' });
  };

  const [selectedProject, setSelectedProject] = useState(null);
  const [modalOrigin, setModalOrigin] = useState(null);

  const openModal = (project, origin = null) => {
    setModalOrigin(origin);
    setSelectedProject(project);
  };

  const closeModal = () => {
    setSelectedProject(null);
  };

  const [activeSection, setActiveSection] = useState('home');
  const navClass = (id) =>
    `relative flex justify-center px-4 pb-2 text-sm hover:text-white transition-all cursor-pointer after:content-[""] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:h-[2px] after:bg-gradient-to-r from-[#2D5CFF] to-[#2D3190] after:transition-all after:duration-300 hover:after:w-full ${activeSection === id ? 'text-white after:w-full' : 'text-gray-300 after:w-0'}`;

  // Header logo ring fills as the page scrolls
  const progressRing = useRef(null);
  useEffect(() => {
    const st = ScrollTrigger.create({
      start: 0,
      end: 'max',
      onUpdate: (self) => {
        if (progressRing.current) progressRing.current.style.strokeDashoffset = String(RING * (1 - self.progress));
      },
    });
    const navTriggers = [
      ['home', '#home', '#home'],
      ['projects', '#projects', projectsRef.current],
      ['about', '#about', '#about'],
      ['contact', '#contact', '#contact'],
    ].map(([id, trigger, endTrigger]) =>
      ScrollTrigger.create({
        trigger,
        endTrigger,
        start: 'top 45%',
        end: 'bottom 45%',
        onToggle: (self) => setActiveSection((cur) => (self.isActive ? id : cur === id ? null : cur)),
      }),
    );
    // the 3D coins drift with parallax as the hero scrolls away
    const heroScroll = ScrollTrigger.create({
      trigger: '#home',
      start: 'top top',
      end: 'bottom top',
      onUpdate: (self) => (coinsState.scroll = self.progress),
    });
    const refresh = () => ScrollTrigger.refresh();
    window.addEventListener('load', refresh);
    document.fonts?.ready.then(refresh);
    return () => {
      st.kill();
      heroScroll.kill();
      navTriggers.forEach((t) => t.kill());
      window.removeEventListener('load', refresh);
    };
  }, []);

  // Card videos only start loading once the Projects section is near the viewport
  // (the poster shows until then); the modal then opens on a warm cache.
  const projectsRef = useRef(null);
  const [projectsVisible, setProjectsVisible] = useState(false);

  useEffect(() => {
    const node = projectsRef.current;
    if (!node || projectsVisible) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setProjectsVisible(true);
        observer.disconnect();
      }
    }, { rootMargin: '200px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [projectsVisible]);


  useLayoutEffect(() => {
    coinsState.intro = 0;
    const nameSplit = SplitText.create('#hero-name', { type: 'words,chars', mask: 'chars' });
    gsap.set(nameSplit.chars, { yPercent: 110 });
    gsap.set('#role-final', { yPercent: 110 });
    const typeSplit = new SplitType('.animate', {
      types: 'lines',
    });
    // lines measured with the fallback font re-wrap once Poppins arrives — split again when the
    // fonts are ready (always before the line reveal, which starts after the photo intro)
    let fontsCancelled = false;
    let linesRevealed = false;
    document.fonts.ready.then(() => {
      if (fontsCancelled) return;
      typeSplit.split();
      // (if the reveal already ran, keep the new lines visible)
      gsap.set('.animate .line', linesRevealed ? { opacity: 1, y: 0 } : { opacity: 0, y: '100%' });
    });

    const ctx = gsap.context(() => {
      const tl = gsap.timeline();
      gsap.set('.header', { y: -100 });
      gsap.set('.mainbtn', { opacity: 0, y: 20 });
      gsap.set('.animate .line', { opacity: 0, y: '100%' });
      gsap.set('.bg-blurred-circle', { opacity: 0 });
      gsap.set('.bg-dotted-circle', { opacity: 0 });
      gsap.set(gsap.utils.toArray('.pic_element_1'), { opacity: 0 });
      gsap.set(gsap.utils.toArray('.pic_element_2'), { opacity: 0 });
      gsap.set(gsap.utils.toArray('.pic_element_3'), { opacity: 0 });
      gsap.set(gsap.utils.toArray('.pic_element_4'), { opacity: 0 });
      gsap.set(gsap.utils.toArray('.pic_element_5'), { opacity: 0 });
      if (window.matchMedia("(min-width: 768px)").matches) {
        gsap.set('.image_photo', { opacity: 0, scale: 0.8, x: -300, y: -50 });
      } else {
        gsap.set('.image_photo', { opacity: 0, scale: 0.8, x: 0, y: 100 });
      };

      gsap.set('.photo-pill', { clipPath: 'inset(50% 50% 50% 50% round 999px)' });
      // EXPERIMENTAL opening: the original photo as a framed 3D portrait travels to its place,
      // then only the portrait dissolves — leaving the cutout already in position
      gsap.set('.photo-img', { opacity: 0 });
      gsap.set('.portrait-card', { autoAlpha: 1, rotationX: 14, rotationY: -20, scale: 0.92, transformPerspective: 1400, filter: 'blur(0px)' });
      tl.to('.image_photo', {
        opacity: 1,
        scale: 1,
        duration: 1.5,
        ease: 'power2.out',
      })
        .to('.portrait-card', { rotationX: 0, rotationY: 0, scale: 1, duration: 1.6, ease: 'power3.out' }, 0)
        // the whole framed portrait glides into place
        .to('.image_photo', {
          x: 0,
          y: 0,
          duration: 1.1,
          ease: 'power3.inOut',
        }, '-=0.1')
        // the cutout is pixel-aligned with the photo, so fading it in on top changes nothing visible…
        .to('.photo-img', { opacity: 1, duration: 0.35, ease: 'none' }, '-=0.5')
        // …then the card, frame and room dissolve away, leaving only you
        .to('.portrait-card', { autoAlpha: 0, filter: 'blur(4px)', duration: 0.75, ease: 'power2.inOut' }, '<0.2')
        // the rest of the hero starts as the portrait finishes leaving
        .add(() => {
          gsap.to(coinsState, { intro: 1, duration: 1.8, ease: 'none' });
          gsap.fromTo('.photo-pill',
            { clipPath: 'inset(50% 50% 50% 50% round 999px)' },
            { clipPath: 'inset(0% 0% 0% 0% round 999px)', duration: 1.6, ease: 'expo.out' });
          linesRevealed = true;
          gsap.to(".animate .line", {
            y: 0,
            opacity: 1,
            duration: 0.8,
            ease: "power3.out",
            stagger: 0.2,
          })
          gsap.to(nameSplit.chars, { yPercent: 0, duration: 1.1, ease: 'expo.out', stagger: 0.03, delay: 0.15 });
          gsap.to('#role-final', { yPercent: 0, duration: 0.9, ease: 'expo.out', delay: 0.35 });
          gsap.to('.header', {
            y: 0,
            duration: 0.4,
            ease: 'power1.out',
          });
          gsap.to('.mainbtn', {
            y: 0,
            opacity: 1,
            duration: 0.4,
            ease: "power3.in",
            clearProps: "transform",
          });
          gsap.to('.bg-blurred-circle', {
            opacity: 1,
            duration: 0.4,
            ease: "power3.in",
          });
          gsap.to('.bg-dotted-circle', {
            opacity: 1,
            duration: 0.4,
            ease: "power3.in",
          });
          gsap.fromTo('#home .bg-dotted-circle', { '--draw': 0 }, {
            '--draw': 360,
            delay: 1,
            duration: 1.8,
            ease: 'power2.inOut',
            stagger: 0.3,
          });
          gsap.to(gsap.utils.toArray('.pic_element_1'), { opacity: 1, duration: 1, ease: "power3.out" });
          gsap.to(gsap.utils.toArray('.pic_element_2'), { opacity: 1, duration: 1, ease: "power3.out" });
          gsap.to(gsap.utils.toArray('.pic_element_3'), { opacity: 1, duration: 1, ease: "power3.out" });
          gsap.to(gsap.utils.toArray('.pic_element_4'), { opacity: 1, duration: 1, ease: "power3.out" });
          gsap.to(gsap.utils.toArray('.pic_element_5'), { opacity: 1, duration: 1, ease: "power3.out" });
          gsap.from(gsap.utils.toArray('.pic_element_1'), { x: -60, y: 275, duration: 1, ease: "power3.out" })
          gsap.from(gsap.utils.toArray('.pic_element_2'), { x: 220, y: 145, duration: 1, ease: "power3.out" })
          gsap.from(gsap.utils.toArray('.pic_element_3'), { x: -165, y: 90, duration: 1, ease: "power3.out" })
          gsap.from(gsap.utils.toArray('.pic_element_4'), { x: 215, y: 290, duration: 1, ease: "power3.out" })
          gsap.from(gsap.utils.toArray('.pic_element_5'), { x: -215, y: -190, duration: 1, ease: "power3.out" })
        }, "-=0.25")
    });

    return () => {
      fontsCancelled = true;
      ctx.revert();
      typeSplit.revert();
      nameSplit.revert();
    };
  }, [languageSelected]);

  // Projects title — the same masked char reveal as the other section titles
  useLayoutEffect(() => {
    const split = SplitText.create('[data-projects-title]', { type: 'words,chars', mask: 'chars' });
    const tween = gsap.from(split.chars, { yPercent: 110, stagger: 0.03, scrollTrigger: { trigger: '[data-projects-title]', start: 'top 85%' } });
    return () => {
      tween.scrollTrigger?.kill();
      tween.kill();
      split.revert();
    };
  }, [languageSelected]);

  const projects = [
    {
      id: 3,
      title: "Mercado de previsão ao vivo sobre o trânsito",
      tags: ["Computer Vision", "Backend", "Prediction Market"],
      img: "/projects/traffic-market-cover.jpg",
      overview: "Transformei uma câmera de trânsito em um mercado de previsão ao vivo. Um modelo de visão computacional (YOLO) detecta e conta cada veículo, quadro a quadro, no feed da câmera. O sistema abre uma rodada, os usuários negociam quantos veículos vão passar naquele período e, quando a rodada termina, o mercado se liquida sozinho com o número real, sem nenhum humano no processo. Está em produção, com usuários reais fazendo negociações reais.",
      functionalities: "Detecção e rastreamento de veículos em tempo real, Rodadas de mercado abertas e liquidadas automaticamente pela contagem real, Transmissão anotada ao vivo em WebRTC, HLS e RTSP, Frota de câmeras escalável, Watchdogs com reinício automático e troca de IP quando um feed degrada.",
      myFunction: "Construí o sistema de ponta a ponta. Detectar carros foi a parte rápida: o trabalho de verdade foi tornar o dado confiável o suficiente para um mercado ser liquidado em cima dele, sem estar errado, congelado ou atrasado. Mantive a contagem estável por dias limpando continuamente a memória do rastreador. Coloquei um buffer de quadros entre a decodificação e o modelo, para que uma queda da fonte vire um congelamento mínimo em vez de uma dessincronização. Tratei latência como requisito: encode por GPU em H.264 Baseline (roda até em celulares antigos) com keyframe a cada segundo, para qualquer espectador se recuperar de uma falha em até 1s. Cada stream é publicado uma única vez e distribuído em WebRTC de baixa latência, então os espectadores nunca tocam a máquina com GPU: a parte de processamento e a de transmissão escalam de forma independente.",
      stackTools:
        <ul className="flex flex-row">
          <TechIcon {...stackIcons.python} />
          <TechIcon {...stackIcons.typescript} />
          <TechIcon {...stackIcons.node} />
          <TechIcon {...stackIcons.linux} />
        </ul>,
      gallery: [
        {
          type: "video",
          src: "/projects/traffic-market.mp4",
          poster: "/projects/traffic-market-cover.jpg",
          alt: "YOLO contando veículos ao vivo no cruzamento de Shibuya",
        },
      ],
    },
    {
      id: 5,
      title: "Roleta de prêmios gamificada para feirão automotivo",
      tags: ["Web App", "Backend", "Gamification"],
      img: "/projects/gs-roulette-cover.jpg",
      overview: "Aplicação web interativa criada para o feirão da GS Motors, onde clientes giram uma roleta para concorrer a prêmios reais. A experiência é totalmente imersiva, com trilha sonora, efeitos sonoros e animações, transformando o momento da venda em um evento memorável e gamificado.",
      functionalities: "Roleta animada com sorteio baseado em dados reais de prêmios, trilha sonora e efeitos sonoros, controles de áudio, tela de resultado e experiência responsiva para totem/tablet.",
      myFunction: "Atuei como engenheiro de software e backend do projeto, responsável por toda a lógica da aplicação. Desenvolvi o registro de inventário de prêmios e o gerenciamento das chances/probabilidades de sorteio, garantindo sorteios justos e baseados em dados reais. Toda a solução de backend foi construída sobre o Supabase (banco de dados, regras e persistência).",
      stackTools:
        <ul className="flex flex-row">
          <TechIcon {...stackIcons.react} />
          <TechIcon {...stackIcons.typescript} />
          <TechIcon {...stackIcons.tailwind} />
          <TechIcon {...stackIcons.supabase} />
        </ul>,
      gallery: [
        {
          type: "video",
          src: "/projects/gs-roulette.mp4",
          poster: "/projects/gs-roulette-cover.jpg",
          alt: "Clique no ícone de som para ouvir a música 🔊",
        },
      ],
    },
    {
      id: 2,
      title: "Aplicativo mobile para postagem e candidaturas de vagas",
      tags: ["Mobile App", "UX/UI"],
      img: hub4you_thumb,
      overview: "Este é um aplicativo mobile de recrutamento voltado para profissionais com mais de 30 anos, conectando empresas e candidatos qualificados. A plataforma oferece um sistema inteligente de compatibilidade que analisa o perfil dos usuários e sugere as melhores oportunidades, criando um processo de candidatura mais assertivo e personalizado.",
      functionalities: "Feed de Vagas, Sistema de Compatibilidade Inteligente, Perfil Profissional Completo, Candidatura Simplificada, Sistema de Notificações, Diretório de Empresas, Publicação de Vagas (Empresas).",
      myFunction: "Atuei como UI/UX Designer responsável por todo o processo de design do aplicativo. Apliquei técnicas e boas práticas consolidadas no mercado de design. Desenvolvi a arquitetura completa, definindo fluxos de navegação e jornadas dos usuários. Criei protótipos interativos de alta fidelidade e todo o design system no Figma, incluindo componentes, padrões de interface e guidelines de usabilidade",
      stackTools:
        <ul className="flex flex-row">
          <TechIcon {...stackIcons.figma} />
        </ul>,
      gallery: [
        { alt: "Login Screen", image: login_hub4you, device: "mobile" },
        { alt: "Feed", image: home, device: "mobile" },
        { alt: "Apply Modal", image: applyModal, device: "mobile" },
        { alt: "New Post Modal", image: newPostModal, device: "mobile" },
        { alt: "Companies Screen", image: company, device: "mobile" },
        { alt: "Notifications Screen", image: emptyNotifications, device: "mobile" },
        { alt: "Notifications Screen", image: notifications, device: "mobile" },
        { alt: "Profile", image: profile, device: "mobile" },
        { alt: "Profile", image: fullProfile, device: "mobile" },
        { alt: "Brand Manual", image: brandManual, device: "mobile" },
      ]
    },
    {
      id: 1,
      title: "Aplicativo mobile para controle e gestão de barbearia",
      tags: ["Mobile App", "UX/UI", "Fullstack", "Design System"],
      img: barbershop_thumb,
      overview: "Este é um aplicativo mobile completo para barbearia, permitindo agendamentos, gestão de clientes e serviços. O projeto inclui um design system robusto para garantir consistência visual e uma ótima experiência de usuário.",
      functionalities: "Sistema de Agendamentos, Gestão de Clientes, Catálogo de Serviços, Painel Administrativo.",
      myFunction: "Atuei como desenvolvedor full stack único do projeto, sendo responsável por todas as etapas do desenvolvimento. No front-end, construí a interface mobile completa com foco em usabilidade e performance, implementando o design system do zero. No back-end, desenvolvi toda a API RESTful, lógica de negócios e integrações necessárias. Também modelei e implementei o banco de dados, definindo estruturas de tabelas, relacionamentos e queries otimizadas. Além disso, gerenciei o ciclo completo de desenvolvimento, desde o planejamento arquitetural até o deploy.",
      stackTools:
        <ul className="flex flex-row">
          <TechIcon {...stackIcons.postgres} />
          <TechIcon {...stackIcons.springBoot} />
          <TechIcon {...stackIcons.figma} />
          <TechIcon {...stackIcons.react} />
          <TechIcon {...stackIcons.tailwind} />
          <TechIcon {...stackIcons.java} />
        </ul>,
      gallery: [
        { alt: "Login Screen", image: login, device: "mobile" },
        { alt: "Home/Services Screen", image: homeServices, device: "mobile" },
        { alt: "Gallery", image: gallery, device: "mobile" },
        { alt: "Professionals Modal", image: professionals, device: "mobile" },
        { alt: "Scheduling Screen", image: schedule, device: "mobile" },
        { alt: "Success Modal", image: confirmationModal, device: "mobile" },
        { alt: "Booking Screen", image: bookings, device: "mobile" },
        { alt: "Schedule Settings", image: scheduleSettings, device: "mobile" },
        { alt: "Modal Schedule Settings", image: scheduleSettingsModal, device: "mobile" },
      ]
    },
  ]

  return (
    <div className='bg-[#05060A] min-h-screen overflow-x-clip'>
      <ClickSpark />
      <LangTransition coverRef={langCover} />
      <div className="grain" aria-hidden />
      <div className="relative z-10">
        <BackgroundCircles />
        <LiquidGlass as='header' sheen={false} radius={0} bezel={14} refraction={40} blur={2.5} className={`header glass-strong glass-bar sticky top-0 flex h-[64px] border-b-[1px] border-[#ffffff0D] z-[98] ${selectedProject ? 'hidden' : ''}`}>
          <div className='max-w-[88rem] mx-auto flex items-center w-full px-6'>
            <div className='group flex items-center justify-start w-full gap-2 cursor-pointer'>
              <span className='relative grid h-11 w-11 shrink-0 place-items-center'>
                <svg className='absolute inset-0 -rotate-90' viewBox='0 0 44 44' aria-hidden>
                  <circle cx='22' cy='22' r='19' fill='none' stroke='rgba(255,255,255,0.08)' strokeWidth='1.5' />
                  <circle ref={progressRing} cx='22' cy='22' r='19' fill='none' stroke='url(#ringGrad)' strokeWidth='1.8' strokeLinecap='round' strokeDasharray={RING} strokeDashoffset={RING} />
                  <defs>
                    <linearGradient id='ringGrad' x1='0' y1='0' x2='44' y2='44' gradientUnits='userSpaceOnUse'>
                      <stop stopColor='#7D9BFF' />
                      <stop offset='1' stopColor='#2D5CFF' />
                    </linearGradient>
                  </defs>
                </svg>
                <img src={logo} className='w-[22px] h-[22px]' alt='logo' />
              </span>
              <div className="overflow-hidden w-fit pl-[22px] -translate-x-[30%]">
                <p className='font-Podkova text-xl text-white -translate-x-full opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-300 ease-in-out'>Dudu</p>
              </div>
            </div>
            <div className='hidden sm:flex content-row items-center justify-center w-full gap-4'>
              <a href='#home' onClick={handleLinkClick} className={navClass('home')}>{t('Início')}</a>
              <a href='#projects' onClick={handleLinkClick} className={navClass('projects')}>{t('Projetos')}</a>
              <a href='#about' onClick={handleLinkClick} className={navClass('about')}>{t('Sobre')}</a>
              <a href='#contact' onClick={handleLinkClick} className={navClass('contact')}>{t('Contato')}</a>
            </div>
            <div className='flex content-row items-center justify-end w-full gap-4'>
              <LangSwitch isEnglish={languageSelected === 'en-US'} onToggle={handleChangeLanguage} />
              <span className='hidden sm:block'>
                <BrandButton size='sm' iconMotion='drop' href={languageSelected === "en-US" ? "/dudu_resume.pdf" : "/dudu_curriculo.pdf"} download icon={<DownloadIcon />}>
                  {t('Currículo')}
                </BrandButton>
              </span>
              <button onClick={() => showSidebar()} className='cursor-pointer p-2 sm:hidden'><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M4 8l16 0" /><path d="M4 16l16 0" /></svg></button>
            </div>
          </div>
        </LiquidGlass>
        <header className={`sidebar fixed backdrop-blur-[30px] shadow-[0px_0px_30px_rgba(227,228,237,0.05)] bg-[rgba(255,255,255,0.01)] border-l-[1px] border-[#ffffff0D] top-0 right-0 h-[100dvh] max-w-[300px] mx-auto flex-col w-full px-6 py-[20px] gap-[50px] z-[99] ${selectedProject ? '!hidden' : 'hidden'}`}>
          <div className='flex flex-row items-center justify-start w-full gap-2'>
            <p className='text-white w-[-webkit-fill-available] leading-[28px]'>Menu</p>
            <button onClick={() => hideSidebar()} className='cursor-pointer'><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M18 6l-12 12" /><path d="M6 6l12 12" /></svg></button>
          </div>
          <div className='flex flex-col w-full h-full gap-6'>
            <a href='#home' className='text-gray-300 hover:text-white transition-all cursor-pointer' onClick={(e) => { handleLinkClick(e); hideSidebar(); }}>{t('Início')}</a>
            <a href='#projects' className='text-gray-300 hover:text-white transition-all cursor-pointer' onClick={(e) => { handleLinkClick(e); hideSidebar(); }}>{t('Projetos')}</a>
            <a href='#about' className='text-gray-300 hover:text-white transition-all cursor-pointer' onClick={(e) => { handleLinkClick(e); hideSidebar(); }}>{t('Sobre')}</a>
            <a href='#contact' className='text-gray-300 hover:text-white transition-all cursor-pointer' onClick={(e) => { handleLinkClick(e); hideSidebar(); }}>{t('Contato')}</a>
          </div>
          <div className='flex flex-row items-center w-full justify-between'>
            <div className='text-[#707070] hover:text-white transition-all cursor-pointer' onClick={() => window.open('https://github.com/Dudu-Brancalhao/', '_blank')} target='_blank' rel='noopener noreferrer'>GitHub</div>
            <div className='text-[#707070] hover:text-white transition-all cursor-pointer' onClick={() => window.open('https://www.linkedin.com/in/eduardo-brancalhao/', '_blank')} target='_blank' rel='noopener noreferrer'>LinkedIn</div>
            <div className='text-[#707070] hover:text-white transition-all cursor-pointer' target='_blank' rel='noopener noreferrer'>CV</div>
          </div>
        </header>
        <div id='home' className='body relative max-w-[88rem] mx-auto md:h-[850px] flex flex-col-reverse md:flex-row items-center pt-[1rem] md:pt-[8rem] pb-[1rem] md:pb-[8rem] gap-12 z-10 px-6'>
          {hasWebGL && (
            <Suspense fallback={null}>
              <Sparkles className='absolute inset-y-0 left-1/2 w-screen -translate-x-1/2' />
            </Suspense>
          )}
          <div className='relative w-full h-full flex flex-col gap-10 md:text-left sm:pt-20'>
            <BgDottedCircle className="top-[0px] left-[-12%] md:h-[200px] md:w-[200px]" degrees={135} />
            <div key={languageSelected} className='flex flex-col gap-4'>
              <div className="animate line font-PoppinsLight text-white text-lg">{t('Olá 👋🏻, eu sou')}</div>
              <div id='hero-name' className='font-PoppinsBold text-white text-3xl sm:text-5xl'>Eduardo Brancalhão</div>
              <div className='relative overflow-hidden pb-1'>
                <div id='role-final' className='font-PoppinsSemiBold text-2xl sm:text-3xl' style={{
                  background: 'linear-gradient(90deg, #2D5CFF 0%, #2D85D6 22%, #7D9BFF 45%, #2D3190 72%, #2D5CFF 100%)',
                  backgroundSize: '200% auto',
                  animation: 'gradientFlow 5s linear infinite',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>
                  {t('Engenheiro de Software')}
                </div>
              </div>
              <div className="animate line text-[#94A3B8] leading-[30px]">
                {languageSelected === "en-US" ? (
                  <>I turn complex product ideas into <span className="font-PoppinsSemiBold text-white">secure, scalable systems</span>, end to end. A backend engineer in <span className="font-PoppinsSemiBold text-white">FinTech & crypto</span> with a frontend and <span className="font-PoppinsSemiBold text-white">UI/UX design</span> background, I can own a feature <span className="font-PoppinsSemiBold text-white">from the Figma file to production</span>.</>
                ) : (
                  <>Transformo ideias complexas de produto em <span className="font-PoppinsSemiBold text-white">sistemas seguros e escaláveis</span>, de ponta a ponta. Engenheiro backend em <span className="font-PoppinsSemiBold text-white">FinTech e cripto</span>, com bagagem em frontend e <span className="font-PoppinsSemiBold text-white">UI/UX design</span>, consigo assumir uma feature <span className="font-PoppinsSemiBold text-white">do Figma até a produção</span>.</>
                )}
              </div>
            </div>
            <div className='mainbtn flex flex-wrap items-center gap-3'>
              <BrandButton href={`mailto:${LINKS.email}`} icon={<ArrowIcon />}>
                {t('Contato')}
              </BrandButton>
              <LiquidGlass as='a' href='#projects' onClick={handleLinkClick} sheen={false} radius={999} bezel={16} refraction={36} className='inline-flex h-14 items-center px-6 text-white transition-transform duration-300 ease-out hover:scale-[1.15]'>
                <span>{t('Ver projetos')}</span>
              </LiquidGlass>
            </div>

            <div className='h-[-webkit-fill-available]'></div>
            <div className='mainbtn inline-flex gap-[12px]'>
              {[
                { href: LINKS.github, label: 'GitHub', Icon: GithubIcon },
                { href: LINKS.linkedin, label: 'LinkedIn', Icon: LinkedinIcon },
              ].map(({ href, label, Icon }) => (
                <div key={label} className='tooltip-container'>
                  <LiquidGlass as='a' href={href} target='_blank' rel='noopener noreferrer' aria-label={label} sheen={false} radius={999} bezel={12} refraction={24} className='grid h-11 w-11 place-items-center text-white/85 hover:text-white transition-transform duration-300 ease-out hover:scale-[1.15]'>
                    <Icon width={18} height={18} />
                  </LiquidGlass>
                  <div role='tooltip' className='tooltip'>{label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className='relative w-full h-[-webkit-fill-available] flex justify-center items-center pt-5 sm:pt-0'>
            <div className='image_photo relative max-w-full md:h-[-webkit-fill-available]'>
              {/* the blue pill, measured from the original photo: 62% wide, full height, 220° alpha gradient */}
              <div
                className='photo-pill absolute top-0 h-full rounded-full'
                style={{ left: '12.78%', width: '62.11%', background: 'linear-gradient(220deg, rgba(43, 48, 143, 0.01) 0%, rgba(46, 93, 255, 0.73) 100%)' }}
              />
              <div
                aria-hidden
                className='portrait-card pointer-events-none absolute'
                style={{ left: '-0.15%', top: '-0.15%', width: '100.1%', height: '100.25%', visibility: 'hidden', transformStyle: 'preserve-3d' }}
              >
                {/* back plate — a tilted brand-gradient layer behind the frame, for depth */}
                <div className='absolute -inset-3 translate-x-4 translate-y-5 rotate-[4deg] rounded-[34px] opacity-50' style={{ background: 'linear-gradient(140deg, #2D5CFF 0%, #2D3190 60%, rgba(45, 49, 144, 0) 100%)' }} />
                {/* frame — frosted border with a light rim */}
                <div
                  className='absolute -inset-3 rounded-[32px]'
                  style={{
                    background: 'linear-gradient(160deg, rgba(255,255,255,0.22), rgba(255,255,255,0.05) 40%, rgba(125,155,255,0.12))',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.45), inset 0 0 0 1px rgba(255,255,255,0.12), 0 40px 90px -30px rgba(0,0,0,0.85), 0 0 70px -15px rgba(45,92,255,0.55)',
                  }}
                />
                <img src={dudu_original} alt='' className='absolute inset-0 h-full w-full rounded-[22px] object-cover' />
                {/* inner bevel — makes the print sit inside the frame */}
                <div className='absolute inset-0 rounded-[22px]' style={{ boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.18), inset 0 2px 10px rgba(0,0,0,0.35)' }} />
              </div>
              <img src={dudu_photo} alt='self-portrait' className='photo-img relative w-full h-auto md:w-auto md:h-full object-contain' />
            </div>
            {hasWebGL ? (
              <Suspense fallback={null}>
                <HeroCoins className='absolute z-[5]' />
              </Suspense>
            ) : (
              <>
                <svg className='pic_element_1 absolute top-0 right-[33%] md:w-[80px] w-[50px] backdrop-blur-[4px]' viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="80" height="80" rx="40" fill="url(#paint0_linear_198_537)" fillOpacity="0.3" />{cloneElement(stackIcons.claude.svg, { x: 18, y: 18, width: 44, height: 44 })}<defs><linearGradient id="paint0_linear_198_537" x1="0" y1="40" x2="80" y2="40" gradientUnits="userSpaceOnUse"><stop stopColor="#2D3190" /><stop offset="1" stopColor="#2D5CFF" /></linearGradient></defs></svg>
                <svg className='pic_element_2 absolute left-[9%] top-[20%] md:w-[80px] w-[50px] backdrop-blur-[4px]' viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="80" height="80" rx="40" fill="url(#paint0_linear_198_546)" fillOpacity="0.3" />{cloneElement(stackIcons.node.svg, { x: 18, y: 18, width: 44, height: 44 })}<defs><linearGradient id="paint0_linear_198_546" x1="0" y1="40" x2="80" y2="40" gradientUnits="userSpaceOnUse"><stop stopColor="#2D3190" /><stop offset="1" stopColor="#2D5CFF" /></linearGradient></defs></svg>
                <svg className='pic_element_3 absolute right-[18%] top-[30%] md:w-[80px] w-[50px] backdrop-blur-[4px]' width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg"><foreignObject x="-4" y="-4" width="88" height="88"><div xmlns="http://www.w3.org/1999/xhtml"></div></foreignObject><g data-figma-bg-blur-radius="4"><rect width="80" height="80" rx="40" fill="url(#paint0_linear_564_3402)" fillOpacity="0.3" /><path d="M40 44.4531C42.4594 44.4531 44.4531 42.4594 44.4531 40C44.4531 37.5406 42.4594 35.5469 40 35.5469C37.5406 35.5469 35.5469 37.5406 35.5469 40C35.5469 42.4594 37.5406 44.4531 40 44.4531Z" fill="#61DAFB" /><path d="M56.9141 32.6562C56.0547 32.3437 55.1563 32.0313 54.2188 31.7578C54.4531 30.8203 54.6484 29.8828 54.8047 28.9844C55.625 23.8281 54.7266 20.1953 52.2266 18.7891C51.4844 18.3594 50.6641 18.1641 49.7266 18.1641C46.9922 18.1641 43.5156 20.1953 40 23.5937C36.4844 20.1953 33.0078 18.1641 30.2734 18.1641C29.3359 18.1641 28.5156 18.3594 27.7734 18.7891C25.2734 20.2344 24.375 23.8672 25.1953 28.9844C25.3516 29.8828 25.5469 30.8203 25.7813 31.7578C24.8438 32.0313 23.9453 32.3047 23.0859 32.6562C18.2031 34.5312 15.5469 37.1094 15.5469 40C15.5469 42.8906 18.2422 45.4688 23.0859 47.3438C23.9453 47.6563 24.8438 47.9688 25.7813 48.2422C25.5469 49.1797 25.3516 50.1172 25.1953 51.0156C24.375 56.1719 25.2734 59.8047 27.7734 61.2109C28.5156 61.6406 29.3359 61.8359 30.2734 61.8359C33.0469 61.8359 36.5234 59.8047 40 56.4063C43.5156 59.8047 46.9922 61.8359 49.7266 61.8359C50.6641 61.8359 51.4844 61.6406 52.2266 61.2109C54.7266 59.7656 55.625 56.1328 54.8047 51.0156C54.6484 50.1172 54.4531 49.1797 54.2188 48.2422C55.1563 47.9688 56.0547 47.6953 56.9141 47.3438C61.7969 45.4688 64.4531 42.8906 64.4531 40C64.4531 37.1094 61.7969 34.5312 56.9141 32.6562ZM51.1328 20.7422C52.7344 21.6797 53.2813 24.5703 52.6172 28.6719C52.5 29.4922 52.3047 30.3516 52.0703 31.25C50.0391 30.7812 47.8906 30.4687 45.625 30.2734C44.2969 28.3984 42.9297 26.7188 41.5625 25.1953C44.4531 22.3437 47.3828 20.3906 49.7656 20.3906C50.2734 20.3906 50.7422 20.5078 51.1328 20.7422ZM46.7578 43.9063C46.0547 45.1563 45.2344 46.4062 44.375 47.6562C42.9297 47.7734 41.4844 47.8125 40 47.8125C38.4766 47.8125 37.0312 47.7734 35.625 47.6562C34.7656 46.4062 33.9844 45.1563 33.2812 43.9063C32.5391 42.6172 31.8359 41.2891 31.2109 40C31.8359 38.7109 32.5391 37.3828 33.2812 36.0938C33.9844 34.8438 34.8047 33.5938 35.6641 32.3438C37.1094 32.2266 38.5547 32.1875 40.0391 32.1875C41.5625 32.1875 43.0078 32.2266 44.4141 32.3438C45.2734 33.5938 46.0547 34.8438 46.7578 36.0938C47.5 37.3828 48.2031 38.7109 48.8281 40C48.1641 41.2891 47.5 42.5781 46.7578 43.9063ZM50 42.6172C50.5859 43.9844 51.0547 45.3125 51.4844 46.6406C50.1562 46.9531 48.75 47.1875 47.2656 47.3828C47.7344 46.6406 48.2422 45.8594 48.6719 45.0391C49.1406 44.2188 49.5703 43.3984 50 42.6172ZM40 53.2031C39.0625 52.1875 38.1641 51.0938 37.3047 49.9609C38.2031 50 39.1016 50.0391 40 50.0391C40.8984 50.0391 41.7969 50 42.6953 49.9609C41.8359 51.0938 40.9375 52.1875 40 53.2031ZM32.7344 47.3438C31.25 47.1484 29.8437 46.9141 28.5156 46.6016C28.9453 45.3125 29.4141 43.9453 30 42.5781C30.4297 43.3594 30.8594 44.1797 31.3281 44.9609C31.7969 45.8203 32.2656 46.5625 32.7344 47.3438ZM30 37.3828C29.4141 36.0156 28.9453 34.6875 28.5156 33.3594C29.8437 33.0469 31.25 32.8125 32.7344 32.6172C32.2656 33.3594 31.7578 34.1406 31.3281 34.9609C30.8594 35.7812 30.4297 36.6016 30 37.3828ZM40 26.7969C40.9375 27.8125 41.8359 28.9062 42.6953 30.0391C41.7969 30 40.8984 29.9609 40 29.9609C39.1016 29.9609 38.2031 30 37.3047 30.0391C38.1641 28.9062 39.0625 27.8125 40 26.7969ZM48.6719 35L47.2656 32.6562C48.75 32.8516 50.1562 33.0859 51.4844 33.3984C51.0547 34.6875 50.5859 36.0547 50 37.4219C49.5703 36.6016 49.1406 35.7812 48.6719 35ZM27.3828 28.6719C26.7188 24.5703 27.2656 21.6797 28.8672 20.7422C29.2578 20.5078 29.7266 20.3906 30.2344 20.3906C32.5781 20.3906 35.5078 22.3047 38.4375 25.1953C37.0703 26.6797 35.7031 28.3984 34.375 30.2734C32.1094 30.4687 29.9609 30.8203 27.9297 31.25C27.6953 30.3516 27.5391 29.4922 27.3828 28.6719ZM17.7344 40C17.7344 38.1641 19.9609 36.2109 23.8672 34.7656C24.6484 34.4531 25.5078 34.1797 26.3672 33.9453C26.9922 35.8984 27.7734 37.9687 28.7109 40.0391C27.7734 42.1094 26.9531 44.1406 26.3672 46.0938C20.9766 44.5312 17.7344 42.1875 17.7344 40ZM28.8672 59.2578C27.2656 58.3203 26.7188 55.4297 27.3828 51.3281C27.5 50.5078 27.6953 49.6484 27.9297 48.75C29.9609 49.2188 32.1094 49.5313 34.375 49.7266C35.7031 51.6016 37.0703 53.2813 38.4375 54.8047C35.5469 57.6563 32.6172 59.6094 30.2344 59.6094C29.7266 59.6094 29.2578 59.4922 28.8672 59.2578ZM52.6172 51.3281C53.2813 55.4297 52.7344 58.3203 51.1328 59.2578C50.7422 59.4922 50.2734 59.6094 49.7656 59.6094C47.4219 59.6094 44.4922 57.6953 41.5625 54.8047C42.9297 53.3203 44.2969 51.6016 45.625 49.7266C47.8906 49.5313 50.0391 49.1797 52.0703 48.75C52.3047 49.6484 52.4609 50.5078 52.6172 51.3281ZM56.1328 45.2344C55.3516 45.5469 54.4922 45.8203 53.6328 46.0547C53.0078 44.1016 52.2266 42.0313 51.2891 39.9609C52.2266 37.8906 53.0469 35.8594 53.6328 33.9063C59.0234 35.4688 62.2656 37.8125 62.2656 40C62.2656 41.8359 60 43.7891 56.1328 45.2344Z" fill="#61DAFB" /></g><defs><clipPath id="bgblur_0_564_3402_clip_path" transform="translate(4 4)"><rect width="80" height="80" rx="40" /></clipPath><linearGradient id="paint0_linear_564_3402" x1="0" y1="40" x2="80" y2="40" gradientUnits="userSpaceOnUse"><stop stopColor="#2D3190" /><stop offset="1" stopColor="#2D5CFF" /></linearGradient></defs></svg>
                <svg className='pic_element_4 absolute top-0 left-[12%] md:w-[30px] w-[18px]' viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="30" height="30" rx="15" fill="url(#paint0_linear_198_554)" fillOpacity="0.5" /><defs><linearGradient id="paint0_linear_198_554" x1="0" y1="15" x2="30" y2="15" gradientUnits="userSpaceOnUse"><stop stopColor="#2D3190" /><stop offset="1" stopColor="#2D5CFF" /></linearGradient></defs></svg>
                <svg className='pic_element_5 absolute bottom-[17%] right-[14%] md:w-[30px] w-[18px] flex -z-10' viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="30" height="30" rx="15" fill="url(#paint0_linear_198_554)" fillOpacity="0.5" /><defs><linearGradient id="paint0_linear_198_554" x1="0" y1="15" x2="30" y2="15" gradientUnits="userSpaceOnUse"><stop stopColor="#2D3190" /><stop offset="1" stopColor="#2D5CFF" /></linearGradient></defs></svg>
              </>
            )}
            <BgDottedCircle className="bottom-[20%] right-[10%] h-[200px] w-[200px] md:h-[350px] md:w-[350px]" degrees={250} />
            <div className="bg-blurred-circle absolute left-[45%] w-[600px] h-[600px] rounded-full -translate-x-1/2 pointer-events-none z-[-10]" />
          </div>
        </div>
        <Manifesto />
        <div id='projects' className='relative mx-auto flex max-w-[1440px] flex-col gap-5 px-5 pt-28 sm:px-10 sm:pt-40'>
          <span className='label text-mist'>(02) — {t('Conheça meu trabalho')}</span>
          <h2 key={languageSelected} data-projects-title className='font-display text-[clamp(3rem,8vw,7.5rem)] font-semibold leading-[0.88] tracking-[-0.045em] text-white'>
            {t('Projetos')}
          </h2>
        </div>

        <div className='body max-w-[1440px] mx-auto flex flex-col md:flex-row items-center pt-16 pb-[3rem] md:pb-[8rem] md:gap-12 gap-[10px] z-10 px-5 sm:px-10'>
          <div ref={projectsRef} className='relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[3rem] w-full h-auto'>
            <BgDottedCircle className="top-[-8%] left-[-6%] md:h-[200px] md:w-[200px]" degrees={135} drawOnScroll />
            {projects?.map((projectItem, index) => {
              return (
                <div key={index} className="flex flex-col gap-[1rem]" onClick={(e) => openModal(projectItem, e.currentTarget.querySelector('img, video'))}>
                  <div className="w-full aspect-square bg-[#181C20] rounded-[16px] border-[1px] border-[#ffffff0D] hover:border-[#2D5CFF] shadow-[0_8px_32px_0_rgba(0,0,0,0.4)] hover:shadow-[0_0px_60px_0_rgba(45,92,255,0.5)] duration-300 ease-in-out hover:scale-105 cursor-pointer">
                    {/* projects with a video play it right on the card — muted, looping, inline */}
                    {projectItem.gallery?.find((g) => g.type === "video") ? (
                      <video
                        src={projectsVisible ? projectItem.gallery.find((g) => g.type === "video").src : undefined}
                        poster={projectItem.img}
                        muted
                        autoPlay
                        loop
                        playsInline
                        preload="auto"
                        aria-label={t(projectItem.title)}
                        className='w-full h-full rounded-[16px] object-cover'
                      />
                    ) : (
                      <img src={projectItem.img} alt={projectItem.img} className='rounded-[16px] md:h-[-webkit-fill-available] object-contain' />
                    )}
                  </div>
                  <div className="flex flex-row gap-[10px]">
                    {projectItem.tags?.map((tag, index) => {
                      return (
                        <div key={index} className={`font-PoppinsBold text-white text-[12px] flex justify-center px-[12px] py-[6px] rounded-[4px] ${index === 0 ? "bg-gradient-to-r from-[#2D5CFF] to-[#2D3190]" : "bg-[#1C1E22]"}`}>
                          {tag.toUpperCase()}
                        </div>
                      )
                    })}
                  </div>
                  <div className="text-lg text-white font-PoppinsSemiBold">{t(projectItem.title)}</div>
                </div>
              )
            })}
            <div className="relative flex flex-col gap-[1.5rem] items-center justify-center w-full aspect-square rounded-[16px]" style={{
              backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3cdefs%3e%3clinearGradient id='g' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3e%3cstop offset='0%25' style='stop-color:rgba(45,92,255, 0.8)' /%3e%3cstop offset='100%25' style='stop-color:rgba(45,49,144, 0.8)' /%3e%3c/linearGradient%3e%3c/defs%3e%3crect width='100%25' height='100%25' fill='none' rx='16' ry='16' stroke='url(%23g)' strokeWidth='3' stroke-dasharray='10, 14' stroke-dashoffset='0' strokeLinecap='square'/%3e%3c/svg%3e")`,
            }}>
              {/* just the icon on the flat card, ringed by the same dashed gradient stroke as the card */}
              <div aria-hidden className='grid h-16 w-16 place-items-center rounded-full' style={{
                backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3cdefs%3e%3clinearGradient id='g' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3e%3cstop offset='0%25' style='stop-color:rgba(45,92,255, 0.8)' /%3e%3cstop offset='100%25' style='stop-color:rgba(45,49,144, 0.8)' /%3e%3c/linearGradient%3e%3c/defs%3e%3crect width='100%25' height='100%25' fill='none' rx='999' ry='999' stroke='url(%23g)' stroke-dasharray='6, 8' stroke-dashoffset='0'/%3e%3c/svg%3e")`,
              }}>
                <svg width="24" height="30" viewBox="0 0 24 30" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2.50684 11.8725L7.10144 5.658C10.0715 1.6395 11.5565 -0.367496 12.9381 0.0555041C14.3241 0.478504 14.3241 2.9445 14.3241 7.872V8.3355C14.3241 10.113 14.3241 10.9995 14.8911 11.5575L14.9226 11.5845C15.5031 12.129 16.4257 12.129 18.2752 12.129C21.6053 12.129 23.2658 12.129 23.8283 13.137C23.8373 13.155 23.8463 13.1685 23.8553 13.1865C24.3863 14.2125 23.4233 15.5175 21.4973 18.123L16.9027 24.342C13.9326 28.3605 12.4476 30.3675 11.0615 29.9445C9.6755 29.5215 9.6755 27.0555 9.6755 22.128V21.6645C9.6755 19.887 9.6755 19.0005 9.10848 18.4425L9.07698 18.4155C8.49647 17.871 7.57395 17.871 5.72441 17.871C2.39433 17.871 0.733796 17.871 0.171283 16.863C0.162283 16.845 0.153282 16.8315 0.144282 16.8135C-0.38673 15.783 0.580792 14.478 2.50684 11.8725Z" fill="url(#paint0_linear_637_673)" /><defs><linearGradient id="paint0_linear_637_673" x1="0" y1="15" x2="24" y2="15" gradientUnits="userSpaceOnUse"><stop stopColor="#2D5CFF" /><stop offset="1" stopColor="#2D3190" /></linearGradient></defs></svg>
              </div>
              <div className="text-lg text-white">{t('Aqui pode ser seu projeto!')}</div>
              <BrandButton href={`mailto:${LINKS.email}`} icon={<ArrowIcon />}>
                {t('Contato')}
              </BrandButton>
            </div>
          </div >
        </div>
        <Workflow />
        <Stack />
        <About projectCount={projects.length} />
        <Modal
          origin={modalOrigin}
          show={!!selectedProject}
          onClose={closeModal}
          size="fullscreen"
          description={
            selectedProject && (
              <>
                <div className="flex flex-col p-10 gap-4 text-white">
                  <h5 className="mt-4 text-2xl font-semibold">{t(selectedProject.title)}</h5>
                  <div className="flex flex-row gap-[10px] flex-wrap">
                    {selectedProject.tags?.map((tag, index) => (
                      <div key={index} className={`font-PoppinsBold text-white text-[12px] flex justify-center px-[12px] py-[6px] rounded-[4px] ${index === 0 ? "bg-gradient-to-r from-[#2D5CFF] to-[#2D3190]" : "bg-[#1C1E22]"}`}>
                        {tag.toUpperCase()}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col ps-[2.5rem] pe-[2.5rem] gap-2 text-white">
                  <p className="font-semibold">{t('Visão geral')}</p>
                  <p className="text-[#94A3B8] mb-4">{t(selectedProject.overview)}</p>
                  <p className="font-semibold">{t('Principais funcionalidades')}</p>
                  <p className="text-[#94A3B8] mb-4">{t(selectedProject.functionalities)}</p>
                  <p className="font-semibold">{t('Meu papel')}</p>
                  <p className="text-[#94A3B8] mb-4">{t(selectedProject.myFunction)}</p>
                </div>
                <div className="flex flex-col items-center gap-4 p-10">
                  <div className='font-PoppinsBold' style={{
                    background: 'linear-gradient(135deg, #2D5CFF 0%, rgba(45, 49, 144, 0.8) 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    textFillColor: 'transparent',
                  }} >
                    Stack & Tools
                  </div>
                  <div className="flex">
                    {selectedProject.stackTools}
                  </div>
                </div>
              </>
            )
          }
          gallery={
            selectedProject && (
              <ul className={`bg-[#1C1E22] custom-scrollbar ${selectedProject.gallery.some((g) => g.type === "video") ? "flex flex-col items-center justify-center gap-6 min-h-screen lg:h-full p-8" : "flex flex-wrap gap-[40px] justify-center max-h-100vh overflow-auto p-8"}`}>
                {selectedProject.gallery.map((projectItem, index) => (
                  <li key={index} className={`flex flex-col items-center gap-4 ${projectItem.type === "video" ? "w-full" : ""}`}>
                    {projectItem.type === "video" ? (
                      <video
                        src={projectItem.src}
                        poster={projectItem.poster}
                        muted
                        autoPlay
                        loop
                        controls
                        playsInline
                        preload="auto"
                        className="w-full max-w-[1200px] max-h-[80vh] rounded-lg"
                      />
                    ) : (
                      <img className={`${projectItem.device === "mobile" ? "w-[320px]" : "w-full"} rounded-lg`} src={projectItem.image} alt={`${projectItem.alt} showcase`} />
                    )}
                    <p className="text-white">{t(projectItem.alt)}</p>
                  </li>
                ))}
              </ul>
            )
          }

        />
        <Contact />
      </div>
    </div>
  );
}

export default App;
